import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './hooks/useAuth';
import './styles/logo-animations.css';

// Public Layout and Pages
import Layout from './components/Layout';
import Home from './pages/Home';
import About from './pages/About';
import Services from './pages/Services';
import Industries from './pages/Industries';
import CaseStudies from './pages/CaseStudies';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import Careers from './pages/Careers';
import Contact from './pages/Contact';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import CookiePolicy from './pages/CookiePolicy';

// Admin Layout and Pages
import AdminLayout from './components/AdminLayout';
import Login from './pages/admin/Login';
import Dashboard from './pages/admin/Dashboard';
import BlogManagement from './pages/admin/BlogManagement';
import BlogEditor from './pages/admin/BlogEditor';

// Create a query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  return <>{children}</>;
};

// Public Route Component (redirects to dashboard if already authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  return <>{children}</>;
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <Router>
            <div className="App">
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<Layout />}>
                  <Route index element={<Home />} />
                  <Route path="about" element={<About />} />
                  <Route path="services" element={<Services />} />
                  <Route path="industries" element={<Industries />} />
                  <Route path="case-studies" element={<CaseStudies />} />
                  <Route path="blog" element={<Blog />} />
                  <Route path="blog/:slug" element={<BlogPost />} />
                  <Route path="careers" element={<Careers />} />
                  <Route path="contact" element={<Contact />} />
                  <Route path="privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="terms-of-service" element={<TermsOfService />} />
                  <Route path="cookie-policy" element={<CookiePolicy />} />
                </Route>

                {/* Admin Login Route */}
                <Route 
                  path="/admin/login" 
                  element={
                    <PublicRoute>
                      <Login />
                    </PublicRoute>
                  } 
                />

                {/* Protected Admin Routes */}
                <Route 
                  path="/admin" 
                  element={
                    <ProtectedRoute>
                      <AdminLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<Navigate to="/admin/dashboard" replace />} />
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route path="blogs" element={<BlogManagement />} />
                  <Route path="blogs/new" element={<BlogEditor />} />
                  <Route path="blogs/edit/:id" element={<BlogEditor />} />
                  <Route path="blogs/categories" element={<div className="p-8 text-center text-gray-600 dark:text-gray-400">Blog Categories - Coming Soon</div>} />
                  <Route path="blogs/tags" element={<div className="p-8 text-center text-gray-600 dark:text-gray-400">Blog Tags - Coming Soon</div>} />
                  <Route path="case-studies" element={<div className="p-8 text-center text-gray-600 dark:text-gray-400">Case Studies Management - Coming Soon</div>} />
                  <Route path="case-studies/new" element={<div className="p-8 text-center text-gray-600 dark:text-gray-400">Case Study Editor - Coming Soon</div>} />
                  <Route path="messages" element={<div className="p-8 text-center text-gray-600 dark:text-gray-400">Messages Management - Coming Soon</div>} />
                  <Route path="users" element={<div className="p-8 text-center text-gray-600 dark:text-gray-400">User Management - Coming Soon</div>} />
                  <Route path="analytics" element={<div className="p-8 text-center text-gray-600 dark:text-gray-400">Analytics - Coming Soon</div>} />
                  <Route path="settings" element={<div className="p-8 text-center text-gray-600 dark:text-gray-400">Settings - Coming Soon</div>} />
                  <Route path="profile" element={<div className="p-8 text-center text-gray-600 dark:text-gray-400">Profile - Coming Soon</div>} />
                  <Route path="notifications" element={<div className="p-8 text-center text-gray-600 dark:text-gray-400">Notifications - Coming Soon</div>} />
                  <Route path="activity" element={<div className="p-8 text-center text-gray-600 dark:text-gray-400">Activity Log - Coming Soon</div>} />
                </Route>

                {/* Catch all route */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </div>
          </Router>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
